import { useState, useEffect } from 'react';
import { toNumber } from '../utils/toNumber';

const useTotals = (groupedItems, formInstructions) => {
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteRowName, setDeleteRowName] = useState(null);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [selectedPresets, setSelectedPresets] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [grandTotal, setGrandTotal] = useState('0.00');
  const [approvedTotal, setApprovedTotal] = useState('0.00');
  const [completedTotal, setCompletedTotal] = useState('0.00');
  const [pendingTotal, setPendingTotal] = useState('0.00');
  const [partsTotal, setPartsTotal] = useState('0.00');
  const [laborTotal, setLaborTotal] = useState('0.00');
  const [rowTotals, setRowTotals] = useState({});
  const [confirmingTask, setConfirmingTask] = useState(null);

  const evaluateFilter = (item, filter) => {
    const { header, operator, value } = filter;
    const itemValue = item[header];
    const values = value.split(',').map(v => v.trim());

    if (operator === 'equals') {
      if (values.length === 1) {
        if (values[0] === 'true') return itemValue === true;
        if (values[0] === 'false') return itemValue === false;
        return String(itemValue) === String(values[0]);
      }
      return values.includes(String(itemValue));
    } else if (operator === 'not_equals') {
      if (values.length === 1) {
        if (values[0] === 'true') return itemValue !== true;
        if (values[0] === 'false') return itemValue !== false;
        return String(itemValue) !== String(values[0]);
      }
      return !values.includes(String(itemValue));
    }
    return false;
  };

  const shouldIncludeItem = (item, conditions, filters) => {
    if (!conditions.length && !filters.length) return true;

    let conditionResult = true;
    if (conditions.length) {
      conditionResult = false;
      conditions.forEach((condition, index) => {
        const isOr = condition.operatorType === 'or';
        const conditionMet = evaluateCondition(item, condition);

        if (index === 0) {
          conditionResult = conditionMet;
        } else if (isOr) {
          conditionResult = conditionResult || conditionMet;
        } else {
          conditionResult = conditionResult && conditionMet;
        }
      });
    }

    let filterResult = true;
    if (filters.length) {
      filterResult = false;
      filters.forEach((filter, index) => {
        const isOr = filter.operatorType === 'or';
        const filterMet = evaluateFilter(item, filter);

        if (index === 0) {
          filterResult = filterMet;
        } else if (isOr) {
          filterResult = filterResult || filterMet;
        } else {
          filterResult = filterResult && filterMet;
        }
      });
    }

    return conditionResult && filterResult;
  };

  const evaluateCondition = (item, condition) => {
    const { header, operator, value } = condition;
    const itemValue = item[header];

    if (operator === 'equals') {
      if (value === 'true') return itemValue === true;
      if (value === 'false') return itemValue === false;
      return String(itemValue) === String(value);
    } else if (operator === 'less_than') {
      return toNumber(itemValue) < toNumber(value);
    } else if (operator === 'greater_than') {
      return toNumber(itemValue) > toNumber(value);
    }
    return false;
  };

  const calculateValue = (item, equation) => {
    if (!equation) return 0;

    const vals = {
      estimate_total: toNumber(item.estimate_total),
      labor_total: toNumber(item.labor_total),
      parts_total: toNumber(item.parts_total),
      labor_hours: toNumber(item.labor_hours),
      labor_rate: toNumber(item.labor_rate || 100),
      quantity: toNumber(item.quantity || 1),
      parts: toNumber(item.parts),
    };

    if (equation.includes('*')) {
      const [a, b] = equation.split('*').map(s => s.trim());
      return vals[a] * vals[b];
    }

    return vals[equation] !== undefined ? vals[equation] : (toNumber(item[equation]) || 0);
  };

  const calculateTotals = () => {
    const allItems = Object.values(groupedItems).flat();
    if (!allItems.length) return;

    const totals = { grand: 0, approved: 0, completed: 0, pending: 0, parts: 0, labor: 0 };

    allItems.forEach(item => {
      ['grand', 'approved', 'completed', 'pending', 'parts', 'labor'].forEach(status => {
        const totalConfig = formInstructions.formTotaling[status];
        if (!totalConfig?.enabled) return;

        const equation = totalConfig.equation || 'estimate_total';
        const value = calculateValue(item, equation);

        if (shouldIncludeItem(item, totalConfig.conditions, totalConfig.filters || [])) {
          totals[status] += value;
        }
      });
    });

    setGrandTotal(totals.grand.toFixed(2));
    setApprovedTotal(totals.approved.toFixed(2));
    setCompletedTotal(totals.completed.toFixed(2));
    setPendingTotal(totals.pending.toFixed(2));
    setPartsTotal(totals.parts.toFixed(2));
    setLaborTotal(totals.labor.toFixed(2));
  };

  useEffect(() => {
    calculateTotals();
  }, [groupedItems, rowTotals]);

  return {
    error,
    setError,
    deleteModalOpen,
    setDeleteModalOpen,
    deleteRowName,
    setDeleteRowName,
    deleteRowId,
    setDeleteRowId,
    flyoutOpen,
    setFlyoutOpen,
    selectedPresets,
    setSelectedPresets,
    selectedCategory,
    setSelectedCategory,
    grandTotal,
    approvedTotal,
    completedTotal,
    pendingTotal,
    partsTotal,
    laborTotal,
    rowTotals,
    setRowTotals,
    confirmingTask,
    setConfirmingTask,
  };
};

export default useTotals;